import { I18N } from '@/app'
import { fakeProfile } from '@/app/testers'
import { createForm, yup } from '@codeleap/common'

import { loginForm } from './login'
import { passwordValidationRegex } from './shared'

const NAME_MAX_LENGTH = 30

const passwordValidation = () =>
  yup.string().required().min(8).max(128).matches(passwordValidationRegex, I18N.t('form.description.password'))

/**
 * `withPassword` comes from the machine, not from the screen.
 *
 * Only an account being created from an address and password needs one; every other provider
 * arrives holding a session. It used to be picked by the form choosing between two ready-made
 * configs from a piece of local state, which was a second source for a question the machine
 * already answers — and the two disagreed. See EPIC-005.
 *
 * `visible` is part of the form's name because the modal keeps it mounted while closed, and a
 * hidden form that still validates blocks the submit button of the one on screen.
 */
export const createSignupForm = (withPassword = false, visible = true) => {
  const base = loginForm()

  return createForm('signup' + (visible ? '' : ':hidden'), {
    email: {
      ...base.config.email,
      defaultValue: fakeProfile.email
    },
    password: {
      ...base.config.password,
      placeholder: I18N.t('form.placeholder.password'),
      label: I18N.t('form.label.password'),
      'aria-label': I18N.t('form.label.password'),
      'aria-required': true,
      defaultValue: fakeProfile.password,
      validate: withPassword ? passwordValidation() : undefined
    },
    name: {
      type: 'text',
      label: I18N.t('form.label.name'),
      'aria-label': I18N.t('form.label.name'),
      'aria-required': true,
      defaultValue: fakeProfile.last_name,
      validate: yup
        .string()
        .required(I18N.t('form.validations.required'))
        .trim(I18N.t('form.validations.required'))
        .test('word-max-length', I18N.t('form.validations.name', { characters: NAME_MAX_LENGTH }), function (value) {
          return value.split(' ').every(x => x.trim().length <= NAME_MAX_LENGTH)
        }),
      placeholder: I18N.t('form.placeholder.fullName')
    },
    repeatPassword: {
      ...base.config.password,
      description: null,
      label: I18N.t('form.label.confirmPassword'),
      'aria-label': I18N.t('form.label.confirmPassword'),
      'aria-required': true,
      defaultValue: fakeProfile.repeatPassword,
      validate: withPassword
        ? (repeatPassword, { password }) => {
            const isValid = repeatPassword === password

            return {
              valid: isValid,
              message: isValid ? '' : "Passwords don't match"
            }
          }
        : null
    },
    referral_code: {
      type: 'text',
      label: I18N.t('form.label.referralCode'),
      placeholder: I18N.t('form.placeholder.referralCode'),
      required: false,
      validate: yup
        .string()
        .transform(value => value?.trim().toUpperCase() || '')
        .when('referral_code', {
          is: value => value && value.length > 0,
          then: schema => schema.min(2, I18N.t('form.validations.min', { min: 2 }))
        })
    },
    terms: {
      type: 'checkbox',
      label: I18N.t('form.label.terms'),
      defaultValue: true,
      validate: value => {
        const accepted = value

        return {
          valid: accepted,
          message: accepted ? '' : I18N.t('form.validations.terms')
        }
      }
    },
    avatar: {
      type: 'file'
    },
    tester: base.config.tester,
    receive_marketing_email: {
      type: 'checkbox',
      defaultValue: true,
      label: I18N.t('form.label.affiliates')
    },
    remember: base.config.remember
  })
}

export const signupForm = (visible = true) => createSignupForm(true, visible)
